/**
 * Compact persistence for Dynamic Packages flight context (navigation / refresh).
 * Replaces storing full availableFlights[] in storage.
 */

import { extractLegsFromDeal, legKey } from "./packageFlightBuilderModel";

export const HR_DP_FLIGHTS_LIST_SNAPSHOT_KEY = "hr_dp_flights_compact_v1";

const SNAPSHOT_VERSION = 1;

function summarizeLeg(leg) {
  if (!leg || typeof leg !== "object") return null;
  const depAir = leg.depAirport;
  const arrAir = leg.arrAirport;
  return {
    depDate: leg.depDate ?? null,
    arrDate: leg.arrDate ?? null,
    depTime: leg.depTime ?? null,
    arrTime: leg.arrTime ?? null,
    flightNumber: leg.flightNumber ?? null,
    airlineId: leg.airlineId ?? leg.airlineCode ?? null,
    supplier: leg.airlineName ?? leg.supplier ?? leg.providerName ?? null,
    depCode:
      typeof depAir === "object" && depAir?.code != null
        ? String(depAir.code).trim().toUpperCase()
        : leg.depCity != null
          ? String(leg.depCity).trim().toUpperCase()
          : null,
    arrCode:
      typeof arrAir === "object" && arrAir?.code != null
        ? String(arrAir.code).trim().toUpperCase()
        : leg.arrCity != null
          ? String(leg.arrCity).trim().toUpperCase()
          : null,
  };
}

/** Self-compose RT: outbound deal + inbound deal each as OW legs. */
function extractLegsFromDepartReturnDeals(selectedDepart, selectedReturn, flightTripType) {
  if (flightTripType === "OneWay" && selectedDepart) {
    const { departLeg, ok } = extractLegsFromDeal(selectedDepart, "OneWay");
    return { departLeg: ok ? departLeg : null, returnLeg: null, ok: Boolean(ok && departLeg) };
  }
  if (!selectedDepart || !selectedReturn) {
    return { departLeg: null, returnLeg: null, ok: false };
  }
  const out = extractLegsFromDeal(selectedDepart, "OneWay");
  const inn = extractLegsFromDeal(selectedReturn, "OneWay");
  const d0 = out.departLeg;
  const d1 = inn.departLeg;
  const ok = Boolean(d0 && d1);
  return { departLeg: d0, returnLeg: d1, ok };
}

function resolveLegsForSnapshot(ctx) {
  const {
    savedDeal,
    selectedDepart,
    selectedReturn,
    flightTripType = "RoundTrip",
  } = ctx;

  if (savedDeal && typeof savedDeal === "object") {
    const r = extractLegsFromDeal(savedDeal, flightTripType);
    if (r.ok) return r;
  }
  if (selectedDepart || selectedReturn) {
    const r2 = extractLegsFromDepartReturnDeals(
      selectedDepart,
      selectedReturn,
      flightTripType
    );
    if (r2.ok) return r2;
  }
  if (selectedDepart) {
    const r3 = extractLegsFromDeal(selectedDepart, flightTripType);
    if (r3.ok) return r3;
  }
  return { departLeg: null, returnLeg: null, ok: false };
}

function resolveDealId(ctx) {
  const { searchPhrase: data, selectedDepart, savedDeal } = ctx;
  return (
    data?.dealId ??
    selectedDepart?.dealId ??
    selectedDepart?.ID ??
    savedDeal?.dealId ??
    savedDeal?.ID ??
    null
  );
}

function resolveSearchId(ctx) {
  const { searchPhrase: data, searchRequestId } = ctx;
  return (
    searchRequestId ??
    data?.search_request_id ??
    data?.compositeHotelBookingRow?.search_request_id ??
    (typeof data?.session === "string" ? data.session : null) ??
    null
  );
}

/**
 * @param {object} ctx
 * @returns {object} Small JSON-safe snapshot (< few KB).
 */
export function buildDynamicPackageFlightsCompactSnapshot(ctx = {}) {
  const { departLeg, returnLeg, ok } = resolveLegsForSnapshot(ctx);
  const dl = summarizeLeg(departLeg);
  const rl = summarizeLeg(returnLeg);
  const dealId = resolveDealId(ctx);
  const searchId = resolveSearchId(ctx);
  const { searchPhrase: data, flightTripType = "RoundTrip" } = ctx;
  const packageId =
    ctx.packageId ?? data?.hotelID ?? data?.hotelId ?? ctx.navPackageId ?? null;

  const selectedDepartKey = dl ? legKey(departLeg) : dealId != null ? String(dealId) : null;
  const selectedReturnKey = rl ? legKey(returnLeg) : null;

  return {
    v: SNAPSHOT_VERSION,
    searchId,
    dealId: dealId != null ? dealId : null,
    packageId: packageId != null ? packageId : null,
    flightTripType,
    selectedDepartKey,
    selectedReturnKey,
    departLegSummary: dl,
    returnLegSummary: flightTripType === "OneWay" ? null : rl,
    linkedFlightsDealPrice:
      ctx.linkedFlightsDealPrice != null && Number.isFinite(Number(ctx.linkedFlightsDealPrice))
        ? Number(ctx.linkedFlightsDealPrice)
        : null,
    sourceCurrency:
      ctx.sourceCurrency != null && String(ctx.sourceCurrency).trim() !== ""
        ? String(ctx.sourceCurrency).trim().toUpperCase()
        : null,
    finalPackagePrice:
      ctx.finalPackagePrice != null && Number.isFinite(Number(ctx.finalPackagePrice))
        ? Number(ctx.finalPackagePrice)
        : null,
    finalTotalIls:
      ctx.finalTotalIls != null && Number.isFinite(Number(ctx.finalTotalIls))
        ? Number(ctx.finalTotalIls)
        : null,
    /** True when legs could not be summarized — UI may refetch flights. */
    legsMissing: !ok,
    savedAt: typeof Date.now === "function" ? Date.now() : undefined,
  };
}

function hydrateLeg(summary) {
  if (!summary || typeof summary !== "object") return null;
  const depAir = summary.depCode ? { code: summary.depCode } : null;
  const arrAir = summary.arrCode ? { code: summary.arrCode } : null;
  return {
    depDate: summary.depDate ?? null,
    arrDate: summary.arrDate ?? null,
    depTime: summary.depTime ?? null,
    arrTime: summary.arrTime ?? null,
    flightNumber: summary.flightNumber ?? null,
    airlineId: summary.airlineId ?? null,
    airlineCode: summary.airlineId ?? null,
    airlineName: summary.supplier ?? null,
    supplier: summary.supplier ?? null,
    depAirport: depAir,
    arrAirport: arrAir,
  };
}

/**
 * @param {object} snap parsed JSON
 * @returns {{
 *   availableFlights: object[]|null,
 *   selectedDepart: object|null,
 *   selectedReturn: object|null,
 *   linkedFlights: object|null,
 *   linkedFlightsDealPrice: number|null,
 * }|null}
 */
export function rehydrateFlightSelectionFromCompactSnapshot(snap) {
  if (!snap || typeof snap !== "object" || Number(snap.v) !== SNAPSHOT_VERSION) {
    return null;
  }

  const dealId = snap.dealId ?? snap.ID ?? null;
  const idStr = dealId != null ? String(dealId) : "hr_dp_compact_hydrated";

  const departLeg = hydrateLeg(snap.departLegSummary);
  const returnLeg =
    snap.flightTripType === "OneWay" ? null : hydrateLeg(snap.returnLegSummary);

  const slots = [];
  if (departLeg) slots.push({ leg: departLeg });
  if (returnLeg) slots.push({ leg: returnLeg });

  const linkedFlights =
    slots.length > 0
      ? {
          flight: slots,
        }
      : null;

  // linkedFlightsDealPrice in SearchContext is always ILS (calculatFlightLDSPrice).
  // Do not tag it with hotel-list sourceCurrency (often USD) — that breaks SSOT evaluate.
  const fare =
    snap.linkedFlightsDealPrice != null &&
    Number.isFinite(Number(snap.linkedFlightsDealPrice))
      ? {
          totalFare: Number(snap.linkedFlightsDealPrice),
          currency: "ILS",
          supplierCurrency: "ILS",
        }
      : undefined;

  const availableFlights = [
    {
      dealId,
      ID: dealId ?? idStr,
      __hrDpCompactHydrated: true,
      __hrDpLegsMissing: snap.legsMissing === true,
      linkedFlights: linkedFlights ? [linkedFlights] : [],
      ...(fare
        ? {
            dealFare: fare,
            fareDetails: { currency: "ILS", commission: {} },
          }
        : {}),
    },
  ];

  return {
    availableFlights,
    selectedDepart: departLeg || null,
    selectedReturn: returnLeg || null,
    linkedFlights,
    linkedFlightsDealPrice:
      snap.linkedFlightsDealPrice != null &&
      Number.isFinite(Number(snap.linkedFlightsDealPrice))
        ? Number(snap.linkedFlightsDealPrice)
        : null,
  };
}

/**
 * @param {object} snap parsed JSON
 * @returns {object[]|null} Single synthetic deal for SearchContext.availableFlights, or null to skip set.
 */
export function rehydrateAvailableFlightsFromCompactSnapshot(snap) {
  const full = rehydrateFlightSelectionFromCompactSnapshot(snap);
  return full?.availableFlights ?? null;
}
